import React from 'react';
import LeadRowActions from './LeadRowActions';
import LeadDetailPanel from './LeadDetailPanel';
import LeadRegionBadge from './LeadRegionBadge';
import s from './Leads.module.scss';

const formatDateTime = (dateTimeString) => {
  if (!dateTimeString) return 'N/A';
  const date = new Date(dateTimeString);
  return date.toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const getLeadCustomerDisplayName = (lead) => {
  const name = String(lead?.customer_name || '').trim();
  if (name) return name;
  const business = String(lead?.business_name || lead?.customer_business_name || lead?.company_name || '').trim();
  if (business) return business;
  return '-';
};

const getLeadCustomerId = (lead) => {
  const id = lead?.customer_id ?? lead?.customer?.id ?? lead?.user_id ?? null;
  return id || '-';
};

const STATUS_STYLES = {
  new: { bg: '#dbeafe', color: '#1e40af', label: 'New' },
  follow_up: { bg: '#ede9fe', color: '#6d28d9', label: 'Follow up' },
  converted: { bg: '#d1fae5', color: '#065f46', label: (lead) => `Converted (#${lead.converted_booking_id || ''})` },
  dropped: { bg: '#fee2e2', color: '#991b1b', label: 'Dropped' },
};

const LeadsTable = ({
  leads, leadsLoading, error, activeTab,
  expandedLeadId, expandedData,
  editingLeadId, editForm, editLeadSaving,
  leadNotes, leadNoteDraft, leadNoteSaving,
  leadActivities, leadConversations, leadConversationLoading, leadConversationError,
  searchTerm,
  onToggleExpand,
  onStartEdit, onSaveEdit, onCancelEdit, onEditFormChange,
  onNoteDraftChange, onAddNote, onSnooze, onEditPricing,
  onStatusChange, onPhoneClick, onCustomerClick,
  pagination,
}) => {
  if (error) {
    return (
      <div className={s.tableCard}>
        <div className={s.errorState}>
          <div className={s.errorIcon}><i className="fa fa-exclamation-circle" /></div>
          <h3>Error Loading Leads</h3>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (leadsLoading && leads.length === 0) {
    return (
      <div className={s.tableCard}>
        <div className={s.loadingState}>
          <div className={s.spinner} />
          <p>Loading leads...</p>
        </div>
      </div>
    );
  }

  if (leads.length === 0) {
    const emptyMessages = {
      due: 'Nothing due today - you\u2019re all caught up!',
      new: 'No new leads in the last 24 hours.',
      follow_up: 'No follow-ups scheduled.',
      converted: 'No converted leads yet.',
      dropped: 'No dropped leads.',
      all: 'No leads yet.',
      long_trip: 'No long-trip leads.',
    };
    return (
      <div className={s.tableCard}>
        <div className={s.emptyState}>
          <div className={s.emptyIcon}><i className="fa fa-inbox" /></div>
          <h3>{emptyMessages[activeTab] || 'No leads found'}</h3>
          {searchTerm && <p>No leads match &ldquo;{searchTerm}&rdquo;</p>}
        </div>
      </div>
    );
  }

  return (
    <div className={s.tableCard}>
      <div className={s.tableWrapper}>
        <table className={s.dataTable}>
          <thead>
            <tr>
              <th><i className="fa fa-hashtag" />Lead / Customer ID</th>
              <th><i className="fa fa-user" />Customer</th>
              <th><i className="fa fa-tag" />Lead Type</th>
              <th><i className="fa fa-info-circle" />Details</th>
              <th>
                <i className={`fa ${activeTab === 'dropped' ? 'fa-archive' : 'fa-calendar'}`} />
                {activeTab === 'dropped' ? 'Dropped On' : 'Contact By'}
              </th>
              <th><i className="fa fa-clock-o" />Created</th>
              <th><i className="fa fa-flag" />Status</th>
              {activeTab === 'dropped' && (
                <th><i className="fa fa-comment-o" />Drop Reason</th>
              )}
              <th><i className="fa fa-cog" />Actions</th>
            </tr>
          </thead>
          <tbody>
            {leads.map((lead) => {
              const statusStyle = STATUS_STYLES[lead.status] || { bg: '#f3f4f6', color: '#374151', label: lead.status };
              const statusLabel = typeof statusStyle.label === 'function' ? statusStyle.label(lead) : statusStyle.label;

              return (
                <React.Fragment key={lead.id}>
                  <tr
                    onClick={() => onToggleExpand(lead)}
                    style={{ cursor: 'pointer', transition: 'background-color 0.2s ease' }}
                    className={expandedLeadId === lead.id ? s.expandedRow : ''}
                  >
                    <td>
                      <div className={s.idBadgeGroup}>
                        <span className={s.idBadge} style={{ background: '#ede9fe', color: '#6d28d9', fontWeight: 700 }}>
                          <i className="fa fa-tag" style={{ fontSize: '0.65rem', marginRight: '3px' }} />
                          {lead.id}
                        </span>
                        <span className={s.idBadge}>
                          <i className="fa fa-user" style={{ fontSize: '0.65rem', marginRight: '3px' }} />
                          {getLeadCustomerId(lead)}
                        </span>
                      </div>
                    </td>
                    <td>
                      <div className={s.customerCell}>
                        <div className={s.customerNameRow}>
                          <span
                            className={s.customerName}
                            onClick={(e) => { e.stopPropagation(); if (onCustomerClick) onCustomerClick(lead); }}
                            style={
                              lead.customer_id || lead.customer?.id
                                ? { cursor: 'pointer', color: '#2563eb', textDecoration: 'underline', textDecorationStyle: 'dotted', textUnderlineOffset: '2px' }
                                : undefined
                            }
                            title={
                              lead.customer_id || lead.customer?.id
                                ? `View details for ${getLeadCustomerDisplayName(lead)}`
                                : undefined
                            }
                          >
                            {getLeadCustomerDisplayName(lead)}
                          </span>
                          <button
                            className={s.quickActionIcon}
                            onClick={(e) => { e.stopPropagation(); onPhoneClick(lead); }}
                            title={`Call ${lead.customer_phone || lead.phone_number || ''}`}
                          >
                            <i className="fa fa-phone" />
                          </button>
                        </div>
                        {lead.business_name && lead.business_name.trim() &&
                          lead.business_name.trim() !== String(lead.customer_name || '').trim() && (
                          <span className={s.businessName}>{lead.business_name}</span>
                        )}
                        {(lead.customer_phone || lead.phone_number) && (
                          <span className={s.customerPhone}>
                            <i className="fa fa-phone" />
                            {lead.customer_phone || lead.phone_number}
                          </span>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className={s.leadTypeCell}>
                        <span style={{
                          display: 'inline-block', padding: '2px 8px', borderRadius: '4px',
                          fontSize: '0.75rem', fontWeight: 600,
                          background: lead.lead_type === 'part_load_interest' ? '#ede9fe' : '#dbeafe',
                          color: lead.lead_type === 'part_load_interest' ? '#6d28d9' : '#1e40af',
                        }}>
                          {lead.lead_type === 'part_load_interest' ? 'Part Load' :
                           lead.lead_type === 'future_booking' ? 'Future Booking' :
                           lead.lead_type === 'general_inquiry' ? 'General Inquiry' :
                           lead.lead_type === 'equipment_interest' ? 'Equipment' :
                           lead.lead_type}
                        </span>
                        <LeadRegionBadge lead={lead} />
                      </div>
                    </td>
                    <td>
                      <div style={{ fontSize: '0.85rem', maxWidth: '250px' }}>
                        {lead.pickup_locality && (
                          <div style={{ fontWeight: 500 }}>{lead.pickup_locality}{lead.pickup_city ? `, ${lead.pickup_city}` : ''}</div>
                        )}
                        {lead.drop_address_ids && lead.drop_address_ids.length > 0 && (
                          <div style={{ color: '#6b7280', fontSize: '0.8rem' }}>
                            <i className="fa fa-arrow-right" /> {lead.drop_address_ids.length} drop{lead.drop_address_ids.length > 1 ? 's' : ''}
                          </div>
                        )}
                        {lead.remarks && (
                          <div style={{ color: '#6b7280', fontSize: '0.8rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {lead.remarks}
                          </div>
                        )}
                        {!lead.pickup_locality && !lead.remarks && (
                          <span style={{ color: '#9ca3af' }}>&mdash;</span>
                        )}
                      </div>
                    </td>
                    <td>
                      {activeTab === 'dropped'
                        ? formatDateTime(lead.dropped_at)
                        : lead.status === 'follow_up' && lead.follow_up_at
                          ? formatDateTime(lead.follow_up_at)
                          : lead.contact_by
                            ? formatDateTime(lead.contact_by)
                            : '-'}
                    </td>
                    <td>{formatDateTime(lead.created_at)}</td>
                    <td>
                      <span style={{
                        display: 'inline-block', padding: '2px 8px', borderRadius: '4px',
                        fontSize: '0.75rem', fontWeight: 600,
                        background: statusStyle.bg, color: statusStyle.color,
                      }}>
                        {statusLabel}
                      </span>
                    </td>
                    {activeTab === 'dropped' && (
                      <td>
                        {lead.dropped_reason ? (
                          <div style={{ maxWidth: '260px', color: '#6b7280', fontSize: '0.85rem' }}>
                            {lead.dropped_reason}
                          </div>
                        ) : (
                          <span style={{ color: '#9ca3af' }}>&mdash;</span>
                        )}
                      </td>
                    )}
                    <td className={s.actionsCell}>
                      <LeadRowActions lead={lead} activeTab={activeTab} onStatusChange={onStatusChange} />
                    </td>
                  </tr>
                  {expandedLeadId === lead.id && (
                    <LeadDetailPanel
                      lead={lead}
                      colSpan={activeTab === 'dropped' ? 9 : 8}
                      expandedData={expandedData}
                      editing={editingLeadId}
                      editForm={editForm}
                      editSaving={editLeadSaving}
                      onStartEdit={onStartEdit}
                      onSaveEdit={onSaveEdit}
                      onCancelEdit={onCancelEdit}
                      onEditFormChange={onEditFormChange}
                      notes={leadNotes}
                      noteDraft={leadNoteDraft}
                      noteSaving={leadNoteSaving}
                      onNoteDraftChange={onNoteDraftChange}
                      onAddNote={onAddNote}
                      activities={leadActivities}
                      conversation={leadConversations}
                      conversationLoading={leadConversationLoading}
                      conversationError={leadConversationError}
                      onSnooze={onSnooze}
                      onToggleExpand={onToggleExpand}
                      onEditPricing={onEditPricing}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      {pagination && pagination.totalPages > 1 && (
        <div className={s.pagination}>
          <button
            disabled={pagination.page <= 1}
            onClick={() => pagination.onPageChange(Math.max(1, pagination.page - 1))}
            className={s.pageBtn}
          >
            <i className="fa fa-chevron-left" /> Previous
          </button>
          <span className={s.pageInfo}>
            Page
            <input
              type="number"
              min={1}
              max={pagination.totalPages}
              value={pagination.pageInput}
              onChange={pagination.onPageInputChange}
              onKeyDown={pagination.onPageInputKeyDown}
              onBlur={pagination.onPageInputBlur}
              className={s.pageInput}
            />
            of {pagination.totalPages}
          </span>
          <button
            disabled={pagination.page >= pagination.totalPages}
            onClick={() => pagination.onPageChange(pagination.page + 1)}
            className={s.pageBtn}
          >
            Next <i className="fa fa-chevron-right" />
          </button>
        </div>
      )}
    </div>
  );
};

export default LeadsTable;
